import axios from "axios";
import api from "./axios";

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/v1/providers`;

export const formatProviderPayload = (form) => {
  const s = (v) => {
    if (v === null || v === undefined) return null;
    const t = String(v).trim();
    return t === "" ? null : t;
  };
  const intOrNull = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  // enums: send null if blank/invalid; backend can enforce rules as needed
  const gender = s(form.gender);
  const ethnicity = s(form.ethnicity);

  return {
    // Personal Information
    firstName: s(form.firstName),
    lastName: s(form.lastName),
    dateOfBirth: s(form.dateOfBirth), // "YYYY-MM-DD" or null
    email: s(form.email),
    phoneNumber: s(form.phoneNumber),
    gender: gender, // null if blank
    ethnicity: ethnicity, // null if blank
    yearsOfExperience: intOrNull(form.yearsOfExperience),

    // Facility Information (top-level)
    facilityName: s(form.facilityName),
    facilityEmail: s(form.facilityEmail),
    facilityWebsite: s(form.facilityWebsite),

    // Document info (keep if you actually send it; otherwise [])
    documentInformation: (form.documentInformation ?? [])
      .map((doc) => ({
        documentType: s(doc.documentType),
        documentCategory: s(doc.documentCategory),
        // include any other fields you use; blank->null via s()
      }))
      .filter((doc) => doc.documentType || doc.documentCategory),

    // Arrays
    facilityLocations: (form.facilityLocations ?? [])
      .map((loc) => ({
        addressLine: s(loc.addressLine),
        city: s(loc.city),
        state: s(loc.state),
        country: s(loc.country),
      }))
      .filter((loc) => loc.addressLine || loc.city || loc.state || loc.country),

    languages: (form.languages ?? [])
      .map((lang) => ({ languageName: s(lang.languageName) }))
      .filter((lang) => lang.languageName),

    educationRecords: (form.educationRecords ?? [])
      .map((edu) => ({
        educationId: edu.educationId ?? null,
        institutionName: s(edu.institutionName),
        degree: s(edu.degree),
        fieldOfStudy: s(edu.fieldOfStudy),
        startYear: intOrNull(edu.startYear),
        endYear: intOrNull(edu.endYear),
      }))
      .filter((e) => e.institutionName || e.degree || e.fieldOfStudy),

    insurances: (form.insurances ?? [])
      .map((ins) => ({
        planName: s(ins.planName),
        planType: s(ins.planType),
      }))
      .filter((ins) => ins.planName || ins.planType),

    boardCertifications: (form.boardCertifications ?? [])
      .map((c) => ({
        certificationId: c.certificationId ?? null,
        name: s(c.name),
        certificationNumber: s(c.certificationNumber),
        issuingBody: s(c.issuingBody),
        certifiedOn: s(c.certifiedOn), // "YYYY-MM-DD" or null
        expiresOn: s(c.expiresOn), // "YYYY-MM-DD" or null
        status: s(c.status),
        requiresRenewal: !!c.requiresRenewal,
        verified: !!c.verified,
      }))
      .filter((c) => c.name || c.certificationNumber || c.issuingBody),

    // ✅ Add specialties to the payload
    specialties: (form.specialties ?? [])
      .map((sp) => ({
        name: s(sp.name),
        description: s(sp.description),
      }))
      .filter((sp) => sp.name),
  };
};

export const signUpProvider = async (providerForm) => {
  const formData = new FormData();

  const providerPayload = formatProviderPayload(providerForm);
  formData.append("provider", JSON.stringify(providerPayload));

  if (providerForm.documentFile) {
    formData.append("documentFile", providerForm.documentFile);
  }

  const response = await api.post(`${API_BASE_URL}/signup`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response.data;
};

//Update languages code
// Merge languages into existing set (server merges on updateSignUpProvider)
export const updateProviderLanguages = async (providerId, languageNames) => {
  const payload = {
    languages: (languageNames ?? [])
      .filter(Boolean)
      .map((name) => ({ languageName: String(name).trim() })),
  };

  const formData = new FormData();
  formData.append("provider", JSON.stringify(payload));

  const resp = await api.put(`${API_BASE_URL}/${providerId}`, formData);
  return resp.data; // ProviderSignUpResponseDTO
};

// Unlink a single language from the provider
export const unlinkLanguage = async (providerId, languageId) => {
  const resp = await api.delete(
    `${API_BASE_URL}/${providerId}/languages/${languageId}`,
  );
  return resp.data; // ProviderSignUpResponseDTO
};

//Update Provider Specialties
// Merge specialties into existing set (server merges on updateSignUpProvider)
export const updateProviderSpecialties = async (providerId, specialtyNames) => {
  // Send only specialties to avoid touching other associations
  const payload = {
    specialties: (specialtyNames ?? [])
      .filter(Boolean)
      .map((name) => ({ name: String(name).trim() })),
  };

  const formData = new FormData();
  formData.append("provider", JSON.stringify(payload));

  const resp = await api.put(`${API_BASE_URL}/${providerId}`, formData);
  return resp.data; // ProviderSignUpResponseDTO
};

// Unlink a single specialty from the provider
export const unlinkSpecialty = async (providerId, specialtyId) => {
  const resp = await api.delete(
    `${API_BASE_URL}/${providerId}/specialties/${specialtyId}`,
  );
  return resp.data; // ProviderSignUpResponseDTO
};

//Update code new
export const updateProviderPersonal = async (id, personal) => {
  const s = (v) => {
    if (v === null || v === undefined) return null;
    const t = String(v).trim();
    return t === "" ? null : t;
  };
  const intOrNull = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  // Only the fields the personal-info modal edits:
  const minimalPayload = {
    firstName: s(personal.firstName),
    lastName: s(personal.lastName),
    dateOfBirth: s(personal.dateOfBirth), // "YYYY-MM-DD"
    email: s(personal.email),
    phoneNumber: s(personal.phoneNumber),
    gender: s(personal.gender),
    ethnicity: s(personal.ethnicity),
    yearsOfExperience: intOrNull(personal.yearsOfExperience),
  };

  const formData = new FormData();
  formData.append("provider", JSON.stringify(minimalPayload));
  // NOTE: no documentFile for personal edits

  // Let axios set the correct multipart boundary automatically:
  const resp = await api.put(`${API_BASE_URL}/${id}`, formData);
  return resp.data; // ProviderSignUpResponseDTO
};

export const updateProvider = async (id, providerForm) => {
  const formData = new FormData();

  const providerPayload = formatProviderPayload(providerForm);
  formData.append("provider", JSON.stringify(providerPayload));

  if (providerForm.documentFile) {
    formData.append("documentFile", providerForm.documentFile);
  }

  const response = await axios.put(`${API_BASE_URL}/${id}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response.data;
};

/*export const deleteProvider = async (id, providerId) => {
  await axios.delete(`${API_BASE_URL}/${id}`, {
    params: { providerId },
  });
};*/

export const deleteProvider = async (id) => {
  await api.delete(`${API_BASE_URL}/${id}`);
};

export const getProvider = async (id) => {
  const response = await axios.get(`${API_BASE_URL}/${id}`);
  return response.data;
};

export const getAllProviders = async () => {
  const response = await axios.get(API_BASE_URL);
  return response.data;
};

export const searchProviders = async (name, limit = 10) => {
  const res = await api.get(`${API_BASE_URL}/lookup`, {
    params: { name, limit },
  });
  return res.data; // plain array, same shape as getAllProviders items
};

export const filterProviders = async (filters, page = 0, size = 20) => {
  const res = await api.post(`${API_BASE_URL}/filter`, filters, {
    params: { page, size },
  });
  return res.data; // { content: [...], totalPages, totalElements, number, size }
};

// ---- Availability helpers ----

// Returns "YYYY-MM-DD" for a given IANA zone (e.g., "America/New_York")
export const getTodayInZone = (zone = "America/New_York") => {
  // Get "now" in the target zone
  const nowStr = new Date().toLocaleString("en-US", { timeZone: zone });
  const now = new Date(nowStr);

  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// Snap "HH:mm" to the nearest 15-min grid (00/15/30/45)
export const snapTo15 = (hhmm) => {
  if (!hhmm) return hhmm;
  const [h, m] = hhmm.split(":").map(Number);
  const mins = h * 60 + m;
  const snapped = Math.round(mins / 15) * 15;
  const H = Math.floor(snapped / 60);
  const M = snapped % 60;
  return `${String(H).padStart(2, "0")}:${String(M).padStart(2, "0")}`;
};

// Convenience: merge current filters with "available today (whole day)"
export const withAvailabilityToday = (filters, zone = "America/New_York") => ({
  ...filters,
  availabilityDate: getTodayInZone(zone),
  availabilityFrom: null,
  availabilityTo: null,
  availabilityZone: zone,
});

// Convenience: merge current filters with "time range today"
export const withAvailabilityTimeRange = (
  filters,
  fromHHmm,
  toHHmm,
  zone = "America/New_York",
) => ({
  ...filters,
  availabilityDate: getTodayInZone(zone),
  availabilityFrom: snapTo15(fromHHmm),
  availabilityTo: snapTo15(toHHmm),
  availabilityZone: zone,
});

// Optionally clear availability (go back to attribute-only filtering)
export const withoutAvailability = (filters) => ({
  ...filters,
  availabilityDate: null,
  availabilityFrom: null,
  availabilityTo: null,
  availabilityZone: null,
  availabilityTelehealth: null,
});

// --- EDUCATION HELPERS --- For Provider Profile display

// A) ADD (merge) — uses your regular PUT /api/providers/{providerId} with minimal payload
export const addEducationRecords = async (
  providerId,
  records /* array of one or more */,
) => {
  const formData = new FormData();
  // map to API shape; keep only valid fields
  const payload = {
    educationRecords: (records ?? []).map((e) => ({
      educationId: e.educationId ?? null, // for "Add New" you'll likely send null/omit
      institutionName: e.institutionName ?? null,
      degree: e.degree ?? null,
      fieldOfStudy: e.fieldOfStudy ?? null,
      startYear: e.startYear ?? null,
      endYear: e.endYear ?? null,
    })),
  };
  formData.append("provider", JSON.stringify(payload));
  const resp = await api.put(`${API_BASE_URL}/${providerId}`, formData);
  return resp.data; // ProviderSignUpResponseDTO
};

// B) EDIT single record — JSON body to /{providerId}/education/{educationId}
export const updateEducationRecord = async (
  providerId,
  educationId,
  request,
) => {
  const resp = await api.put(
    `${API_BASE_URL}/${providerId}/education/${educationId}`,
    {
      institutionName: request.institutionName ?? null,
      degree: request.degree ?? null,
      fieldOfStudy: request.fieldOfStudy ?? null,
      startYear: request.startYear ?? null,
      endYear: request.endYear ?? null,
    },
  );
  return resp.data; // ProviderSignUpResponseDTO
};

// C) DELETE (unlink) — DELETE /{providerId}/education/{educationId}/unlink
export const unlinkEducationRecord = async (providerId, educationId) => {
  const resp = await api.delete(
    `${API_BASE_URL}/${providerId}/education/${educationId}/unlink`,
  );
  return resp.data; // ProviderSignUpResponseDTO
};

// --- BOARD CERTIFICATIONS HELPERS ---

// A) ADD (merge) — uses your regular PUT /api/providers/{providerId} with minimal payload
export const addBoardCertifications = async (providerId, certs /* array */) => {
  const formData = new FormData();
  const payload = {
    boardCertifications: (certs ?? []).map((c) => ({
      certificationId: c.certificationId ?? null, // null/omit for new
      name: c.name ?? null,
      certificationNumber: c.certificationNumber ?? null,
      issuingBody: c.issuingBody ?? null,
      certifiedOn: c.certifiedOn ?? null, // "YYYY-MM-DD"
      expiresOn: c.expiresOn ?? null, // "YYYY-MM-DD"
      status: c.status ?? null,
      requiresRenewal: !!c.requiresRenewal,
      verified: !!c.isVerified,
    })),
  };
  formData.append("provider", JSON.stringify(payload));
  const resp = await api.put(`${API_BASE_URL}/${providerId}`, formData);
  return resp.data; // ProviderSignUpResponseDTO
};

// B) EDIT a single certification
// If you have a dedicated endpoint, use it:
//   PUT /api/providers/{providerId}/board-certifications/{certificationId}
// Otherwise, fallback to merge PUT with one item including certificationId.
export const updateBoardCertification = async (
  providerId,
  certificationId,
  req,
) => {
  // Try dedicated endpoint first; if your backend doesn't have it, comment this out and use merge below.
  try {
    const resp = await api.put(
      `${API_BASE_URL}/${providerId}/certifications/${certificationId}`,
      {
        name: req.name ?? null,
        certificationNumber: req.certificationNumber ?? null,
        issuingBody: req.issuingBody ?? null,
        certifiedOn: req.certifiedOn ?? null,
        expiresOn: req.expiresOn ?? null,
        status: req.status ?? null,
        requiresRenewal: !!req.requiresRenewal,
        verified: !!req.isVerified,
      },
    );
    return resp.data;
  } catch (e) {
    // Fallback: merge via main PUT
    const formData = new FormData();
    const payload = {
      boardCertifications: [
        {
          certificationId,
          name: req.name ?? null,
          certificationNumber: req.certificationNumber ?? null,
          issuingBody: req.issuingBody ?? null,
          certifiedOn: req.certifiedOn ?? null,
          expiresOn: req.expiresOn ?? null,
          status: req.status ?? null,
          requiresRenewal: !!req.requiresRenewal,
          verified: !!req.verified,
        },
      ],
    };
    formData.append("provider", JSON.stringify(payload));
    const resp = await api.put(`${API_BASE_URL}/${providerId}`, formData);
    return resp.data;
  }
};

// C) DELETE (unlink)
export const unlinkBoardCertification = async (providerId, certificationId) => {
  // If your backend uses /unlink suffix, adjust below accordingly.
  const resp = await api.delete(
    `${API_BASE_URL}/${providerId}/certifications/${certificationId}`,
  );
  return resp.data; // ProviderSignUpResponseDTO
};

// ---- FACILITY helpers ----

// ADD (merge) one or more facilities via main PUT
export const addFacilities = async (
  providerId,
  facilities /* array of addresses */,
  org /* {facilityName, facilityEmail, facilityWebsite} */,
) => {
  const formData = new FormData();

  // Top-level org fields
  const payload = {
    facilityName: org?.facilityName ?? null,
    facilityEmail: org?.facilityEmail ?? null,
    facilityWebsite: org?.facilityWebsite ?? null,

    // Only address fields inside the array
    facilityLocations: (facilities ?? []).map((f) => ({
      addressLine: f.addressLine ?? null,
      city: f.city ?? null,
      state: f.state ?? null,
      country: f.country ?? "United States",
    })),
  };

  formData.append("provider", JSON.stringify(payload));

  const resp = await api.put(`${API_BASE_URL}/${providerId}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return resp.data; // ProviderSignUpResponseDTO
};

// EDIT (update in place) — JSON body, NOT multipart
export const updateFacility = async (providerId, locationId, dto) => {
  // dto should match FacilityLocationDTO on the backend
  const body = {
    addressLine: dto.addressLine ?? null,
    city: dto.city ?? null,
    state: dto.state ?? null,
    country: dto.country ?? null,
    facilityName: dto.facilityName ?? null,
    facilityEmail: dto.facilityEmail ?? null,
    facilityWebsite: dto.facilityWebsite ?? null,
  };
  const resp = await api.put(
    `${API_BASE_URL}/${providerId}/facility-locations/${locationId}`,
    body,
  );
  return resp.data; // ProviderSignUpResponseDTO
};

// DELETE / unlink facility location
export const unlinkFacility = async (providerId, locationId) => {
  const resp = await api.delete(
    `${API_BASE_URL}/${providerId}/facility-locations/${locationId}`,
  );
  return resp.data; // ProviderSignUpResponseDTO
};

// ---- INSURANCE helpers ----

// Add (merge) one or more insurance rows via main PUT (multipart)
export const addInsurances = async (
  providerId,
  insurances /* [{planName, planType}] */,
) => {
  const formData = new FormData();
  const payload = {
    insurances: (insurances ?? []).map((i) => ({
      planName: i.planName ?? null,
      planType: i.planType ?? null,
    })),
  };
  formData.append("provider", JSON.stringify(payload));
  const resp = await api.put(`${API_BASE_URL}/${providerId}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return resp.data; // ProviderSignUpResponseDTO
};

// Delete/unlink by catalog planId (from DTO on the profile)
export const unlinkInsurance = async (providerId, planId) => {
  const resp = await api.delete(
    `${API_BASE_URL}/${providerId}/insurances/${encodeURIComponent(planId)}`,
  );
  return resp.data; // ProviderSignUpResponseDTO
};

// --------- HELPER METHODS FOR Checking NPI and VERIFYING ------
// keep digits only
function cleanNpi(npi) {
  return String(npi || "").replace(/\D/g, "");
}

/**
 * GET /api/providers/by-npi/{npi}
 * Returns ProviderPreviewDTO (public)
 */
export async function getProviderByNpi(npi) {
  const cleaned = cleanNpi(npi);
  if (!cleaned) throw new Error("NPI is required");

  const res = await api.get(`${API_BASE_URL}/by-npi/${cleaned}`);
  return res.data; // ProviderPreviewDTO
}

export const PROVIDER_VERIFICATION_DOCUMENT_TYPES = [
  "GOVERNMENT_ID",
  "MEDICAL_LICENSE",
  "DEA_LICENSE",
  "BOARD_CERTIFICATION",
  "NURSING_LICENSE",
  "PHYSICIAN_ASSISTANT_LICENSE",
  "THERAPIST_LICENSE",
  "EMPLOYMENT_VERIFICATION",
  "FACILITY_BADGE",
  "BUSINESS_CARD",
  "MALPRACTICE_INSURANCE",
  "OTHER",
];

export async function uploadProviderVerificationDocument(
  npi,
  documentType,
  file,
) {
  const cleaned = cleanNpi(npi);
  if (!cleaned) throw new Error("NPI is required");
  if (!documentType) throw new Error("Document type is required");
  if (!file) throw new Error("Verification document is required");

  const form = new FormData();
  form.append("documentType", documentType);
  form.append("file", file);

  const res = await api.post(
    `${API_BASE_URL}/by-npi/${cleaned}/verification-document`,
    form,
    {
      headers: { "Content-Type": "multipart/form-data" },
    },
  );
  return res.data; // DocumentResponseDTO
}

/**
 * POST /api/providers/by-npi/{npi}/claim
 * Requires PROVIDER role
 * Body: { email }
 */
export async function claimProviderByNpi(npi, claimRequest = {}) {
  const cleaned = cleanNpi(npi);
  if (!cleaned) throw new Error("NPI is required");

  const res = await api.post(
    `${API_BASE_URL}/by-npi/${cleaned}/claim`,
    claimRequest,
  );

  return res.data; // ProviderSignUpResponseDTO (full)
}

/**
 * OPTIONAL (for later): POST /api/providers/{providerId}/unclaim
 * Requires PROVIDER role
 */
export async function unclaimProvider(providerId) {
  if (!providerId) throw new Error("providerId is required");
  await api.post(`${API_BASE_URL}/${providerId}/unclaim`);
}

// PUT /api/v1/providers/{id}/profile-picture  (multipart)
export const uploadProviderProfilePicture = async (providerId, file) => {
  const form = new FormData();
  form.append("photo", file);
  const res = await api.put(
    `${API_BASE_URL}/${providerId}/profile-picture`,
    form,
  );
  return res.data; // returns the new URL string
};

// DELETE /api/v1/providers/{id}/profile-picture
export const deleteProviderProfilePicture = async (providerId) => {
  await api.delete(`${API_BASE_URL}/${providerId}/profile-picture`);
};

// PATCH /api/v1/providers/{id}/accepting-patients?accepting=true|false
export const updateAcceptingNewPatients = async (providerId, accepting) => {
  await api.patch(`${API_BASE_URL}/${providerId}/accepting-patients`, null, {
    params: { accepting },
  });
};

export const getAcceptingInsurance = async (providerId) => {
  const res = await api.get(
    `${API_BASE_URL}/${providerId}/accepting-insurance`,
  );
  return res.data;
};

export const updateAcceptingInsurance = async (providerId, accepting) => {
  const res = await api.patch(
    `${API_BASE_URL}/${providerId}/accepting-insurance`,
    null,
    { params: { accepting } },
  );
  return res.data;
};

// ── Pricing API ─────────────────────────────────────────────────────────────
export const getProviderPricing = async (providerId) => {
  const res = await api.get(`${API_BASE_URL}/${providerId}/pricing`);
  return res.data;
};

export const upsertProviderPricing = async (providerId, payload) => {
  const res = await api.post(`${API_BASE_URL}/${providerId}/pricing`, payload);
  return res.data;
};

export const deleteProviderPricing = async (providerId, pricingId) => {
  await api.delete(`${API_BASE_URL}/${providerId}/pricing/${pricingId}`);
};

// ── Payments API ─────────────────────────────────────────────────────────────
const PAYMENTS_BASE = `${process.env.REACT_APP_API_BASE_URL}/api/v1/payments`;

export const createSubscriptionSession = async () => {
  const res = await api.post(`${PAYMENTS_BASE}/subscription/session`);
  return res.data; // { checkoutUrl }
};

export const verifySubscriptionSession = async (sessionId) => {
  const res = await api.post(`${PAYMENTS_BASE}/subscription/verify`, {
    sessionId,
  });
  return res.data; // { subscriptionStatus }
};

export const createDepositSession = async (appointmentId) => {
  const res = await api.post(`${PAYMENTS_BASE}/deposit/session`, {
    appointmentId,
  });
  return res.data; // { checkoutUrl }
};

// ── Subscription status ───────────────────────────────────────────────────────
export const getProviderSubscription = async (providerId) => {
  const res = await api.get(`${API_BASE_URL}/${providerId}/subscription`);
  return res.data;
};

export const cancelProviderSubscription = async (providerId) => {
  const res = await api.post(
    `${API_BASE_URL}/${providerId}/subscription/cancel`,
  );
  return res.data;
};
